import { updateTask, useGovernmentIdentifiers } from '@commenda/api';
import {
  pairHasOpenIssue,
  useTaskActivitiesForServiceRequest,
} from '@commenda/api/tasks';
import { Button, Icons, useToggle } from '@commenda/kakashi';
import { NonTimezoneSensitiveDate } from '@commenda/utils';
import { TaskParty, TaskStatus } from '@prisma/client';
import { ServiceRequestTaskLabel } from '../../common/labels';
import { ViewDetailsModal } from '../../common/modals/ViewDetailsModal/ViewDetailsModal';
import { ServiceRequestTaskIconConfig } from '../../common/taskIconConfig';
import { ServiceRequestTaskCard } from '../../layout/components/taskCard/ServiceRequestTaskCard';
import {
  ServiceRequestTaskCardConfig,
  TCommonTaskProp,
  TIncorporationInfoTaskMetadata,
} from '../../types';
import { CaCorpIncorporationTask } from '../config';
import { IncorporationDetailsModal } from './modal/IncorporationDetailsModal';
import { IncorporationDetailsSchemaFields } from './schema/incorporation-details-schema';

export const IncorporationDetails = ({
  task,
  context,
  isTimelineView,
  displayNumber,
}: TCommonTaskProp) => {
  const { role, serviceRequest: data } = context;

  const incorporationDetailsModalVisibility = useToggle();
  const viewIncorporationDetailsModalVisibility = useToggle();

  const metadata = task?.metadata as TIncorporationInfoTaskMetadata;

  const { data: giResp } = useGovernmentIdentifiers(data.corporationId);
  const governmentIdentifiers = giResp?.governmentIdentifiers ?? [];

  const { data: activityFeed } = useTaskActivitiesForServiceRequest(data?.id);

  // Own task is agent-side; the SR-wide wait keys on the fill/review questionnaire pair.
  const fillTask = data?.taskGroups
    ?.flatMap((taskGroup) => taskGroup.tasks)
    ?.find(
      (srt) =>
        srt.legacyServiceRequestTaskTaskId ===
        CaCorpIncorporationTask.FILL_CORPORATION_DETAILS
    );
  const reviewTask = data?.taskGroups
    ?.flatMap((taskGroup) => taskGroup.tasks)
    ?.find(
      (srt) =>
        srt.legacyServiceRequestTaskTaskId ===
        CaCorpIncorporationTask.REVIEW_CORPORATION_DETAILS
    );
  const hasOpenReportedIssue = pairHasOpenIssue(
    activityFeed?.activities,
    fillTask,
    reviewTask
  );

  const getRowsForViewDetails = () => {
    if (!metadata?.incorporationInfo) return [];
    const rows = [
      {
        label: "Entity's final name",
        value: metadata.incorporationInfo.corporationName || '',
      },
      {
        label: 'Date of incorporation',
        value: metadata.incorporationInfo.formationDate
          ? NonTimezoneSensitiveDate.fromISO8601String(
              metadata.incorporationInfo.formationDate
            ).format('MMM D, YYYY')
          : '',
      },
      {
        label: 'Total number of shares',
        value: metadata.incorporationInfo.authorizedShares?.toString() ?? '',
      },
      // Government identifiers, if loaded
      ...((governmentIdentifiers || []).map((gi) => ({
        label: gi.displayName,
        value: gi.value || '',
      })) as { label: string; value: string }[]),
    ];
    return rows;
  };

  const config: ServiceRequestTaskCardConfig = {
    getLeftIcon: (status) => ServiceRequestTaskIconConfig[status],
    getStateConfig: (state) => {
      const stateMap = {
        [TaskParty.CLIENT]: {
          [TaskStatus.COMPLETED]: {
            actions: metadata?.incorporationInfo ? (
              <Button
                size="sm"
                variant="secondary"
                onClick={viewIncorporationDetailsModalVisibility.show}
              >
                View <Icons.ArrowNarrowRight className="size-4" />
              </Button>
            ) : null,
            label: (
              <ServiceRequestTaskLabel
                label="Incorporation details updated by Commenda"
                status={task.status}
                timestamp={task.updatedAt}
              />
            ),
          },
          [TaskStatus.TO_DO]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel label="Commenda updates details for Incorporation" />
            ),
          },
          [TaskStatus.BACKLOG]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel label="Commenda updates details for Incorporation" />
            ),
          },
        },
        [TaskParty.AGENT]: {
          [TaskStatus.COMPLETED]: {
            actions: metadata?.incorporationInfo ? (
              <Button
                size="sm"
                variant="secondary"
                onClick={viewIncorporationDetailsModalVisibility.show}
              >
                View <Icons.ArrowNarrowRight className="size-4" />
              </Button>
            ) : null,
            label: (
              <ServiceRequestTaskLabel
                label="Incorporation details entered"
                status={task.status}
                timestamp={task.updatedAt}
              />
            ),
          },
          [TaskStatus.TO_DO]: {
            actions: (
              <Button
                size="sm"
                onClick={incorporationDetailsModalVisibility.toggle}
              >
                Start <Icons.ArrowUpRight className="size-4" />
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel label="Enter incorporation details" />
            ),
          },
          [TaskStatus.BACKLOG]: {
            actions: (
              <Button size="sm" disabled onClick={() => {}} variant="primary">
                Start <Icons.ArrowUpRight className="size-4" />
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel label="Enter incorporation details" />
            ),
          },
        },
      };

      if (
        hasOpenReportedIssue &&
        state.status !== TaskStatus.COMPLETED &&
        stateMap[state.role]
      ) {
        return {
          actions: null,
          label: (
            <ServiceRequestTaskLabel
              label="Client fixing the reported issues"
              status={task.status}
              timestamp={task.updatedAt}
            />
          ),
        };
      }

      return (
        stateMap[state.role]?.[state.status] ?? {
          label: task.title,
          actions: null,
        }
      );
    },
  };

  return (
    <div>
      <ServiceRequestTaskCard
        task={task}
        state={{ role, status: task.status }}
        config={config}
        context={context}
        isTimelineView={isTimelineView}
        displayNumber={displayNumber}
      />
      <IncorporationDetailsModal
        serviceRequest={context.serviceRequest}
        mutateServiceRequest={context.mutate}
        isOpen={incorporationDetailsModalVisibility.visible}
        setIsOpen={incorporationDetailsModalVisibility.hide}
        onConfirm={async (formData: IncorporationDetailsSchemaFields) => {
          await updateTask({
            id: task.id,
            data: {
              status: TaskStatus.COMPLETED,
              metadata: {
                incorporationInfo: {
                  corporationName: formData.corporationName,
                  formationDate: formData.formationDate,
                  authorizedShares: formData.shareCount,
                },
              },
            },
          });
        }}
      />
      {viewIncorporationDetailsModalVisibility.visible ? (
        <ViewDetailsModal
          isOpen={viewIncorporationDetailsModalVisibility.visible}
          onClose={viewIncorporationDetailsModalVisibility.hide}
          title="Incorporation details"
          content={getRowsForViewDetails()}
          className="max-w-[600px]"
        />
      ) : null}
    </div>
  );
};
